/**
 * CycleReviewExperience — Phase 2 "season finale" cycle-close artifact (Spanish).
 *
 * A lighter, optimistic sibling of End of an Era: this is a CHAPTER ending,
 * not a farewell. Dark editorial aesthetic (reuses EoaE typography) with a
 * warm amber/emerald palette and a forward-looking "next chapter" close.
 * Rendered inside a modal overlay; client-facing copy = 100% Spanish.
 */
import React from 'react';
import { motion } from 'framer-motion';
import { X, Trophy, TrendingUp, ArrowRight, Quote } from 'lucide-react';

const reveal = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1] },
};

const Stat = ({ value, label }) => (
  <div className="text-center">
    <div className="font-anton text-white text-4xl sm:text-5xl tracking-tight leading-none">{value}</div>
    <div className="font-oswald uppercase tracking-widest text-amber-200/60 text-[10px] sm:text-xs mt-2">{label}</div>
  </div>
);

const CycleReviewExperience = ({ report, onClose, onDismiss }) => {
  if (!report) return null;
  const prs = report.top_prs || [];
  const deltas = (report.progression_deltas || []).filter((d) => d.delta_kg > 0);
  const milestones = report.milestones || [];
  const next = report.next_chapter;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 sm:p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="client-cycle-report-modal"
    >
      <motion.div
        {...reveal}
        onClick={(e) => e.stopPropagation()}
        className="eoae-root relative w-full max-w-xl my-4 rounded-3xl overflow-hidden bg-[#0a0a0a] ring-1 ring-amber-500/20 max-h-[94vh] overflow-y-auto"
        data-testid="cycle-review-experience"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 text-white/60 hover:text-white"
          data-testid="client-report-close"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Hero */}
        <div className="relative px-7 pt-12 pb-8 bg-[radial-gradient(circle_at_50%_-20%,rgba(245,158,11,0.18),transparent_60%)]">
          <p className="font-oswald uppercase tracking-[0.4em] text-amber-400 text-xs sm:text-sm mb-4">
            Capítulo completado
          </p>
          <h1 className="font-anton uppercase text-white leading-[0.95] text-5xl sm:text-6xl capitalize">
            {report.focus_es || 'Bloque'}
          </h1>
          <p className="font-manrope text-amber-100/70 text-base mt-3">
            {report.weeks ? `${report.weeks} ${report.weeks === 1 ? 'semana' : 'semanas'} de trabajo` : 'Bloque finalizado'}
          </p>
        </div>

        <div className="px-7 pb-8 space-y-8">
          {/* Numbers */}
          <div className="grid grid-cols-4 gap-3 py-2" data-testid="cycle-review-stats">
            <Stat value={report.sessions_completed ?? '—'} label="Sesiones" />
            <Stat value={report.adherence_percent != null ? `${report.adherence_percent}%` : '—'} label="Adherencia" />
            <Stat value={report.weekly_frequency_actual != null ? `${report.weekly_frequency_actual}×` : '—'} label="Frecuencia" />
            <Stat value={report.deloads_in_cycle ?? 0} label="Descargas" />
          </div>

          {/* Achievements */}
          {milestones.length > 0 && (
            <div data-testid="cycle-review-milestones">
              <h3 className="font-oswald uppercase tracking-widest text-amber-300 text-sm mb-3">Logros del bloque</h3>
              <div className="flex flex-wrap gap-2">
                {milestones.map((m, i) => (
                  <span key={i} className="flex items-center gap-2 bg-amber-500/15 border border-amber-400/30 rounded-full px-3 py-1.5 text-amber-100 text-sm">
                    <span>{m.icon}</span> {m.label}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* PRs */}
          {prs.length > 0 && (
            <div data-testid="cycle-review-prs">
              <h3 className="font-oswald uppercase tracking-widest text-amber-300 text-sm mb-3 flex items-center gap-2">
                <Trophy className="w-4 h-4" /> Récords de este bloque
              </h3>
              <div className="space-y-1.5">
                {prs.map((pr, i) => (
                  <div key={i} className="flex justify-between items-center bg-white/[0.04] rounded-lg px-4 py-2.5">
                    <span className="font-manrope text-white text-sm truncate mr-3">{pr.exercise_name}</span>
                    <span className="font-oswald text-amber-300 text-sm whitespace-nowrap">{pr.reps} × {pr.weight} kg</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Progression */}
          {deltas.length > 0 && (
            <div>
              <h3 className="font-oswald uppercase tracking-widest text-amber-300 text-sm mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" /> Tu progreso
              </h3>
              <div className="space-y-1.5">
                {deltas.map((d, i) => (
                  <div key={i} className="flex justify-between items-center bg-white/[0.04] rounded-lg px-4 py-2.5">
                    <span className="font-manrope text-white text-sm truncate mr-3">{d.exercise_name}</span>
                    <span className="font-manrope text-emerald-400 text-sm whitespace-nowrap">
                      {d.start_best.weight} → {d.end_best.weight} kg (+{d.delta_pct}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AI reflection (or deterministic recap fallback) */}
          {(report.ai_reflection || report.recap) && (
            <div className="border-l-2 border-amber-400/40 pl-4" data-testid="cycle-review-reflection">
              <p className="font-cormorant italic text-white/90 text-xl sm:text-2xl leading-relaxed">
                {report.ai_reflection || report.recap}
              </p>
            </div>
          )}

          {/* Coach note */}
          {report.client_notes && (
            <div className="bg-amber-900/15 border border-amber-400/20 rounded-xl px-4 py-3" data-testid="cycle-review-coach-note">
              <div className="flex items-center gap-2 text-amber-300 text-xs font-oswald uppercase tracking-widest mb-1.5">
                <Quote className="w-3.5 h-3.5" /> Mensaje de tu entrenador
              </div>
              <p className="font-manrope text-amber-50 text-sm italic">{report.client_notes}</p>
            </div>
          )}

          {/* Next chapter */}
          {next && (
            <div className="rounded-2xl bg-gradient-to-br from-emerald-500/15 to-transparent border border-emerald-400/25 px-5 py-5" data-testid="cycle-review-next">
              <div className="flex items-center gap-2 text-emerald-300 text-xs font-oswald uppercase tracking-widest mb-2">
                <ArrowRight className="w-4 h-4" /> Lo que viene · {next.title}
              </div>
              <p className="font-manrope text-white/85 text-sm leading-relaxed">{next.direction}</p>
              {next.goals?.length > 0 && (
                <ul className="mt-3 space-y-1.5">
                  {next.goals.map((g, i) => (
                    <li key={i} className="font-manrope text-emerald-100/80 text-sm flex items-start gap-2">
                      <span className="text-emerald-400 mt-0.5">›</span> {g}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <button
            onClick={onDismiss}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-black font-semibold font-manrope hover:opacity-90 transition"
            data-testid="client-report-dismiss"
          >
            ¡Listo para el siguiente bloque!
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default CycleReviewExperience;
